import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router'
import { routes } from 'types/routes'
import { PageTab } from 'layouts/page-layout/types.ts'
import PageLayout from 'layouts/page-layout/PageLayout.tsx'
import useGetRouteLayout from 'hooks/useGetRouteLayout.ts'
import { Layouts } from 'layouts/types.ts'
import { FaqSlugB2B, FaqSlugB2C, FaqTags } from 'types/faq.ts'
import { useEffect, useMemo } from 'react'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'
import useGetAccessRight from 'hooks/useSelectAccessRight.ts'

const Billing = () => {
  // URL Param
  const { id } = useParams<{ id: string }>()

  // Access rights
  const billingCyclesAccessRights = useGetAccessRight(ACCESS_RIGHT_KEYS.billingCycles)
  const instalmentAccessRights = useGetAccessRight(ACCESS_RIGHT_KEYS.instalment)

  // Layout
  const { layout } = useGetRouteLayout()

  // i18n
  const { t } = useTranslation('billing')

  // Router
  const { pathname } = useLocation()
  const navigate = useNavigate()

  /**
   * Generates the tabs & return them
   *
   * @return {PageTab[]}
   */
  const getTabs = (): PageTab[] => {
    const tabs: PageTab[] = [{ label: t(($) => $.tabs.invoices), path: routes.BILLING_INVOICES }]

    // Push billing cycles tab if allowed
    if (Object.values(billingCyclesAccessRights.canAccess).some(Boolean)) {
      tabs.push({ label: t(($) => $.tabs.billingCycles), path: routes.BILLING_CYCLES })
    }

    // Push instalment tab if allowed
    if (instalmentAccessRights.canAccess) {
      tabs.push({ label: t(($) => $.tabs.instalment), path: routes.BILLING_INSTALMENT })
    }

    return tabs
  }

  // Constants
  const withSwitcher: string[] = [routes.BILLING_INSTALMENT, routes.BILLING_CYCLES]
  const faqTagsToHide = useMemo<FaqTags[]>(() => {
    const tags: FaqTags[] = []
    if (!billingCyclesAccessRights.canAccess.current && !billingCyclesAccessRights.canAccess.previous) {
      tags.push(FaqTags.BILLING_CYCLES)
    }
    if (!instalmentAccessRights.canAccess) tags.push(FaqTags.INSTALMENTS)
    return tags
  }, [billingCyclesAccessRights, instalmentAccessRights])

  // Redirect to invoices if the user lands on the general billing route
  useEffect(() => {
    if (pathname === routes.BILLING) {
      void navigate(routes.BILLING_INVOICES, { replace: true })
    }
  }, [pathname, navigate])

  return layout === Layouts.EDIT_LAYOUT || id ? (
    <Outlet />
  ) : (
    <PageLayout
      title={t(($) => $.nav.billing, {
        ns: 'common'
      })}
      tabs={getTabs()}
      description={pathname.includes(routes.BILLING_INSTALMENT) ? t(($) => $.instalment.description) : undefined}
      faqCategories={{ b2c: [FaqSlugB2C.INVOICES_AND_PAYMENTS], b2b: [FaqSlugB2B.INVOICES] }}
      tagsToHide={faqTagsToHide}
      withSwitcher={withSwitcher.includes(pathname)}
    >
      <Outlet />
    </PageLayout>
  )
}

export default Billing
